import { sanitizeBreadcrumbAction } from './breadcrumbSanitizer';
import { logError } from './ErrorTrackingService';
import type { SessionManager } from '@/lib/SessionTracking/SessionManager';

type ConsoleLevel = 'info' | 'warn' | 'error';

let instrumentationRefs = 0;
let sessionManagerRef: SessionManager | null = null;
let consolePatched = false;
let clickHandler: ((event: MouseEvent) => void) | null = null;
let keydownHandler: ((event: KeyboardEvent) => void) | null = null;
let windowErrorHandler: ((event: ErrorEvent) => void) | null = null;
let rejectionHandler: ((event: PromiseRejectionEvent) => void) | null = null;
const lastGlobalError = { signature: '', timestamp: 0 };

const originalConsole: Partial<
  Record<ConsoleLevel, (...args: unknown[]) => void>
> = {};

const getConsoleRef = (): Console | undefined => {
  if (typeof globalThis === 'undefined') {
    return undefined;
  }
  return globalThis.console;
};

const isHTMLElement = (value: unknown): value is HTMLElement =>
  typeof window !== 'undefined' && value instanceof HTMLElement;

const dedupeWindow = {
  lastAction: '',
  lastTimestamp: 0,
  isDuplicate(action: string) {
    const now = Date.now();
    if (this.lastAction === action && now - this.lastTimestamp < 400) {
      return true;
    }
    this.lastAction = action;
    this.lastTimestamp = now;
    return false;
  },
};

const pendingOutcomeTimers = new Map<string, number>();

const parseIntStrict = (value?: string | null): number | undefined => {
  if (!value) return undefined;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const stableTokenFrom = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  const token = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
  return token || undefined;
};

const describeElement = (
  target: HTMLElement
): { action: string; metadata: Record<string, unknown> } => {
  const tag = target.tagName.toLowerCase();
  const testId = target.getAttribute('data-testid');
  const role = target.getAttribute('role');
  const explicitActionId =
    target.dataset?.sessionActionId || target.dataset?.sessionEvent;
  const stableAction =
    stableTokenFrom(explicitActionId) ??
    stableTokenFrom(testId) ??
    stableTokenFrom(target.id) ??
    stableTokenFrom(role) ??
    stableTokenFrom(tag) ??
    'element';

  const text = (target.textContent || '')
    .trim()
    .replace(/\s+/g, ' ')
    .slice(0, 80);

  const action = sanitizeBreadcrumbAction(`element:${stableAction}`);

  return {
    action,
    metadata: {
      tag,
      id: target.id || undefined,
      testId: testId || undefined,
      ariaLabel: target.getAttribute('aria-label') || undefined,
      role: target.getAttribute('role') || undefined,
      text: text || undefined,
    },
  };
};

const recordUserInteraction = (
  element: HTMLElement,
  source: 'click' | 'key',
  keyPressed?: string
) => {
  if (!sessionManagerRef) return;

  const { action, metadata } = describeElement(element);
  if (!action || dedupeWindow.isDuplicate(`${source}:${action}`)) {
    return;
  }

  const dataset = element.dataset ?? {};
  const actionId =
    dataset.sessionActionId ||
    dataset.sessionEvent ||
    String(metadata.testId ?? '') ||
    String(metadata.id ?? '') ||
    action;
  const sourceContext =
    dataset.sessionSourceContext ||
    (typeof window !== 'undefined' ? window.location.pathname : undefined);
  const actionValue =
    dataset.sessionActionValue ||
    metadata.testId ||
    metadata.id ||
    metadata.ariaLabel ||
    metadata.role ||
    metadata.tag;
  const actionDetail =
    dataset.sessionActionDetail || (source === 'click' ? 'click' : 'keyboard');
  const expectedNext = dataset.sessionExpectedNext || 'SYSTEM_FLOW';
  const requireOutcome =
    dataset.sessionExpectOutcome === 'true' ||
    typeof dataset.sessionExpectedNext === 'string';
  const outcomeTimeoutMs =
    parseIntStrict(dataset.sessionOutcomeTimeoutMs) ?? 3000;

  sessionManagerRef.recordUserAction(actionId, {
    interaction_source: source,
    action_detail: actionDetail,
    source_context: sourceContext,
    action_value: actionValue,
    expected_next_event: expectedNext,
    element: metadata,
    key: source === 'key' ? keyPressed : undefined,
    action_id: actionId,
  });

  if (requireOutcome && actionId) {
    const existing = pendingOutcomeTimers.get(actionId);
    if (typeof existing === 'number') {
      window.clearTimeout(existing);
    }
    const timeoutId = window.setTimeout(() => {
      pendingOutcomeTimers.delete(actionId);
      sessionManagerRef?.recordUserAction(`${actionId}:no_response`, {
        interaction_source: source,
        action_detail: 'no_response',
        source_context: sourceContext,
        action_value: actionValue,
        expected_next_event: expectedNext,
        element: metadata,
        action_id: actionId,
        severity: 'WARN',
      });
    }, outcomeTimeoutMs);
    pendingOutcomeTimers.set(actionId, timeoutId);
  }
};

const shouldThrottleGlobalError = (signature: string): boolean => {
  const now = Date.now();
  if (
    lastGlobalError.signature === signature &&
    now - lastGlobalError.timestamp < 3000
  ) {
    return true;
  }
  lastGlobalError.signature = signature;
  lastGlobalError.timestamp = now;
  return false;
};

const installGlobalHandlers = () => {
  if (windowErrorHandler || rejectionHandler) return;

  windowErrorHandler = (event: ErrorEvent) => {
    const message = event.message || 'Unhandled error';
    const signature = `${message}:${event.filename}:${event.lineno}`;
    if (shouldThrottleGlobalError(signature)) return;
    void sessionManagerRef?.flushBufferedEvents();
    void logError({
      message,
      severity: 'error',
      eventName: event.error?.name ?? 'WindowError',
      sessionId: sessionManagerRef?.getSessionId(),
      userId: sessionManagerRef?.getUserId() ?? undefined,
      platform: 'WEB',
      eventPayload: {
        error_name: event.error?.name ?? 'WindowError',
        stack_trace: event.error?.stack,
        location: {
          url: window.location.href,
          pathname: window.location.pathname,
        },
        metadata: {
          colno: event.colno,
          lineno: event.lineno,
          filename: event.filename,
          type: event.type,
        },
        breadcrumbs: sessionManagerRef?.getBreadcrumbs() ?? [],
      },
    });
  };

  rejectionHandler = (event: PromiseRejectionEvent) => {
    const reason =
      event.reason instanceof Error
        ? event.reason.message
        : String(event.reason ?? 'unhandled rejection');
    const signature = `rejection:${reason}`;
    if (shouldThrottleGlobalError(signature)) return;
    void sessionManagerRef?.flushBufferedEvents();
    void logError({
      message: reason,
      severity: 'error',
      eventName:
        (event.reason instanceof Error && event.reason.name) ||
        'UnhandledRejection',
      sessionId: sessionManagerRef?.getSessionId(),
      userId: sessionManagerRef?.getUserId() ?? undefined,
      platform: 'WEB',
      eventPayload: {
        error_name:
          (event.reason instanceof Error && event.reason.name) ||
          'UnhandledRejection',
        stack_trace:
          event.reason instanceof Error ? event.reason.stack : undefined,
        location: {
          url: window.location.href,
          pathname: window.location.pathname,
        },
        metadata: { type: event.type },
        breadcrumbs: sessionManagerRef?.getBreadcrumbs() ?? [],
      },
    });
  };

  window.addEventListener('error', windowErrorHandler);
  window.addEventListener('unhandledrejection', rejectionHandler);
};

const removeGlobalHandlers = () => {
  if (windowErrorHandler) {
    window.removeEventListener('error', windowErrorHandler);
    windowErrorHandler = null;
  }
  if (rejectionHandler) {
    window.removeEventListener('unhandledrejection', rejectionHandler);
    rejectionHandler = null;
  }
};

const patchConsole = () => {
  if (consolePatched) return;

  const levels: ConsoleLevel[] = ['info', 'warn', 'error'];
  const consoleRef = getConsoleRef();
  if (!consoleRef) {
    return;
  }

  levels.forEach((level) => {
    originalConsole[level] = consoleRef[level];
    consoleRef[level] = (...args: unknown[]) => {
      try {
        const rawMessage =
          typeof args[0] === 'string' ? args[0] : JSON.stringify(args[0] ?? '');
        const message = sanitizeBreadcrumbAction(
          `${level.toUpperCase()}: ${rawMessage}`.slice(0, 200)
        );

        sessionManagerRef?.recordConsole(level, message);
      } catch {
        // Fail silently; never block console output
      }

      originalConsole[level]?.apply(consoleRef, args);
    };
  });

  consolePatched = true;
};

const unpatchConsole = () => {
  if (!consolePatched) return;
  const consoleRef = getConsoleRef();
  if (!consoleRef) {
    return;
  }
  (['info', 'warn', 'error'] as ConsoleLevel[]).forEach((level) => {
    if (originalConsole[level]) {
      consoleRef[level] = originalConsole[level]!;
    }
  });
  consolePatched = false;
};

export const initializeGlobalErrorTracking = (
  sessionManager: SessionManager | null
) => {
  if (typeof window === 'undefined' || !sessionManager) {
    return () => {};
  }

  sessionManagerRef = sessionManager;
  instrumentationRefs += 1;

  patchConsole();
  installGlobalHandlers();

  if (!clickHandler) {
    clickHandler = (event: MouseEvent) => {
      const path = event.composedPath();
      const target = path.find((node) => isHTMLElement(node)) as
        | HTMLElement
        | undefined;
      if (!target) return;
      recordUserInteraction(target, 'click');
    };
    document.addEventListener('click', clickHandler, { capture: true });
  }

  if (!keydownHandler) {
    keydownHandler = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const target = event.target;
      if (!isHTMLElement(target)) return;
      recordUserInteraction(target, 'key', event.key);
    };
    document.addEventListener('keydown', keydownHandler, true);
  }

  return () => {
    instrumentationRefs = Math.max(0, instrumentationRefs - 1);
    if (instrumentationRefs === 0) {
      if (clickHandler) {
        document.removeEventListener('click', clickHandler, { capture: true });
        clickHandler = null;
      }
      if (keydownHandler) {
        document.removeEventListener('keydown', keydownHandler, true);
        keydownHandler = null;
      }
      unpatchConsole();
      removeGlobalHandlers();
      sessionManagerRef = null;
      dedupeWindow.lastAction = '';
      dedupeWindow.lastTimestamp = 0;
      pendingOutcomeTimers.forEach((id) => window.clearTimeout(id));
      pendingOutcomeTimers.clear();
    }
  };
};
